import { useRouter } from "@tanstack/react-router";
import { Sonner } from "@wags/ui/sonner.tsx";
import { type ReactNode, useRef, useSyncExternalStore } from "react";
import { AnimatedBottomNav } from "@/components/shared/AnimatedBottomNav";
import { BottomNav } from "@/components/shared/BottomNav";
import {
    ChatNavigationStateSync,
    markNavRevealed,
    useChatController,
    useChatFabModeWhileMobile,
} from "@/features/kits-conversations";
import { bottomNavHidesOnScroll } from "@/lib/ui/bottomNav";
import { useBottomNavSuppressed } from "@/lib/ui/bottomNavSuppression";
import { shellNavigationLayout } from "./shell-navigation-layout";
import { closeProviderClaim } from "@/features/providers";

function ChatFabLifter() {
    useChatFabModeWhileMobile("above-bottom-nav");
    return null;
}

export function AppShellNavigation({
    pathname,
    children,
}: {
    pathname: string;
    children: (layout: {
        reserveBottomNavSpace: boolean;
        navigation: ReactNode;
    }) => ReactNode;
}) {
    const router = useRouter();
    const landingPathname = useRef(pathname);
    useSyncExternalStore(
        () =>
            router.subscribe("onResolved", (event) => {
                if (event.pathChanged) closeProviderClaim();
                if (event.toLocation.pathname !== landingPathname.current) {
                    markNavRevealed();
                }
            }),
        () => 0,
        () => 0,
    );
    const chatOpen = useChatController((state) => state.chatOpen);
    const reachOutSheetOpen = useChatController(
        (state) => !!state.reachOutSheet,
    );
    const reachOutSheetExiting = useChatController(
        (state) => state.reachOutSheetExiting,
    );
    const bottomNavSuppressed = useBottomNavSuppressed();
    const { showBottomNav, reserveBottomNavSpace } = shellNavigationLayout({
        pathname,
        bottomNavSuppressed,
        chatOpen,
        reachOutSheetOpen,
        reachOutSheetExiting,
    });
    const navigation = (
        <>
            <ChatNavigationStateSync />
            <AnimatedBottomNav
                show={showBottomNav}
                hideOnScroll={bottomNavHidesOnScroll(pathname)}
            >
                <BottomNav />
            </AnimatedBottomNav>
            {showBottomNav && <ChatFabLifter />}
            <Sonner
                position="bottom-center"
                offset={{
                    bottom: showBottomNav
                        ? "calc(env(safe-area-inset-bottom) + 68px)"
                        : "24px",
                }}
                mobileOffset={{
                    bottom: showBottomNav
                        ? "calc(env(safe-area-inset-bottom) + 68px)"
                        : "24px",
                }}
            />
        </>
    );
    return children({ reserveBottomNavSpace, navigation });
}
